"use client";

import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import {
    PlusCircle,
    Server,
    X,
    Terminal,
    Globe,
    ExternalLink,
    Trash2,
    CheckCircle,
    Plus,
    Cog,
    Edit2,
    Eye,
    EyeOff,
    AlertTriangle,
    RefreshCw,
    Play,
    Square,
    Search,
    Upload,
    Download,
    FileJson,
    Settings,
    Clock
} from "lucide-react";
import { toast } from "sonner";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from "./ui/accordion";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "./ui/tabs";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { KeyValuePair, MCPServer, ServerStatus, useMCP } from "@/lib/context/mcp-context";
import { stopAllSandboxes, listActiveSandboxes, getSandboxStatus } from "@/app/actions";

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "./ui/tooltip";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

// Default template for a new MCP server
const INITIAL_NEW_SERVER: Omit<MCPServer, 'id'> = {
    name: '',
    url: '',
    type: 'sse',
    command: 'node',
    args: [],
    env: [],
    headers: []
};

// JSON Import functionality
const parseClaudeDesktopConfig = (jsonText: string): Omit<MCPServer, 'id'>[] => {
    const trimmedText = jsonText.trim();
    
    // Handle raw URLs
    if (trimmedText.startsWith('http://') || trimmedText.startsWith('https://')) {
        try {
            const url = new URL(trimmedText);
            
            // Determine server type and extract name
            let serverName = '';
            const serverType: 'sse' | 'stdio' = 'sse';
            
            if (url.hostname === 'server.smithery.ai') {
                // Extract server name from Smithery URL path (e.g., "@upstash/context7-mcp" from "/@upstash/context7-mcp/mcp")
                const pathParts = url.pathname.split('/').filter(Boolean);
                
                // Find the "/mcp" part and extract everything before it as the server name
                const mcpIndex = pathParts.indexOf('mcp');
                if (mcpIndex !== -1 && mcpIndex > 0) {
                    const fullServerName = pathParts.slice(0, mcpIndex).join('/');
                    // Remove "@" from the beginning if it exists
                    const cleanServerName = fullServerName.startsWith('@') ? fullServerName.slice(1) : fullServerName;
                    serverName = `${cleanServerName}`;
                } else {
                    serverName = 'Smithery Server';
                }
            } else {
                // For other URLs, use hostname as name
                serverName = url.hostname.replace(/^www\./, '');
            }
            
            const server: Omit<MCPServer, 'id'> = {
                name: serverName,
                url: trimmedText,
                type: serverType,
                command: '',
                args: [],
                env: [],
                headers: []
            };
            
            return [server];
        } catch (error) {
            throw new Error(`Invalid URL: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    
    // Handle JSON formats
    try {
        const config = JSON.parse(trimmedText);
        const servers: Omit<MCPServer, 'id'>[] = [];
        
        // Handle Claude Desktop format
        if (config.mcpServers) {
            Object.entries(config.mcpServers).forEach(([name, serverConfig]: [string, any]) => {
                // Convert environment variables from object to array format
                const env: KeyValuePair[] = [];
                if (serverConfig.env) {
                    Object.entries(serverConfig.env).forEach(([key, value]) => {
                        env.push({ key, value: String(value) });
                    });
                }
                
                const server: Omit<MCPServer, 'id'> = {
                    name,
                    url: serverConfig.url || '',
                    type: serverConfig.command ? 'stdio' : 'sse',
                    command: serverConfig.command,
                    args: serverConfig.args || [],
                    env,
                    headers: []
                };
                
                servers.push(server);
            });
        }
        // Handle array format
        else if (Array.isArray(config)) {
            config.forEach((serverConfig: any) => {
                const server: Omit<MCPServer, 'id'> = {
                    name: serverConfig.name || '',
                    url: serverConfig.url || '',
                    type: serverConfig.type || (serverConfig.command ? 'stdio' : 'sse'),
                    command: serverConfig.command || '',
                    args: serverConfig.args || [],
                    env: serverConfig.env || [],
                    headers: serverConfig.headers || []
                };
                servers.push(server);
            });
        }
        // Handle single server format
        else if (config.name || config.url || config.command) {
            const server: Omit<MCPServer, 'id'> = {
                name: config.name || '',
                url: config.url || '',
                type: config.type || (config.command ? 'stdio' : 'sse'),
                command: config.command || '',
                args: config.args || [],
                env: config.env || [],
                headers: config.headers || []
            };
            servers.push(server);
        }
        
        return servers;
    } catch (error) {
        throw new Error(`Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
};

interface MCPServerManagerProps {
    servers: MCPServer[];
    onServersChange: (servers: MCPServer[]) => void;
    selectedServers: string[];
    onSelectedServersChange: (serverIds: string[]) => void;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

// Check if a key name might contain sensitive information
const isSensitiveKey = (key: string): boolean => {
    const sensitivePatterns = [
        /key/i, 
        /token/i, 
        /secret/i, 
        /password/i, 
        /pass/i,
        /auth/i,
        /credential/i
    ];
    return sensitivePatterns.some(pattern => pattern.test(key));
};

// Mask a sensitive value
const maskValue = (value: string): string => {
    if (!value) return '';
    if (value.length < 8) return '••••••';
    return value.substring(0, 3) + '•'.repeat(Math.min(10, value.length - 4)) + value.substring(value.length - 1);
};

// Update the StatusIndicator to use Tooltip component
const StatusIndicator = ({ status, onClick, hoverInfo }: { 
    status?: ServerStatus, 
    onClick?: () => void,
    hoverInfo?: string
}) => {
    const isClickable = !!onClick;
    const hasHoverInfo = !!hoverInfo;
    
    const className = `flex-shrink-0 flex items-center gap-1 ${isClickable ? 'cursor-pointer' : ''}`;
    
    const statusIndicator = (status: ServerStatus | undefined) => {
        switch (status) {
            case 'connected':
                return (
                    <div className={className} onClick={onClick}>
                        <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                        <span className="text-xs text-success hover:underline">Connected</span>
                    </div>
                );
            case 'connecting':
                return (
                    <div className={className} onClick={onClick}>
                        <div className="w-2 h-2 rounded-full bg-warning animate-pulse" />
                        <span className="text-xs text-warning">Connecting</span>
                    </div>
                );
            case 'error':
                return (
                    <div className={className} onClick={onClick}>
                        <AlertTriangle className="w-3 h-3 text-destructive" />
                        <span className="text-xs text-destructive hover:underline">Error</span>
                    </div>
                );
            case 'disconnected':
            default:
                return (
                    <div className={className} onClick={onClick}>
                        <div className="w-2 h-2 rounded-full bg-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Disconnected</span>
                    </div>
                );
        }
    };
    
    // Use Tooltip if we have hover info
    if (hasHoverInfo) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    {statusIndicator(status)}
                </TooltipTrigger>
                <TooltipContent side="top" align="center" className="max-w-[300px] break-all text-wrap">
                    {hoverInfo}
                </TooltipContent>
            </Tooltip>
        );
    }
    
    // Otherwise just return the status indicator
    return statusIndicator(status);
};

// Sandbox Status Component
const SandboxStatus = ({ server, isVisible }: { server: MCPServer; isVisible: boolean }) => {
    const [sandboxState, setSandboxState] = useState<string>('');
    const { stopServer } = useMCP();

    // Debug logging to help understand visibility
    console.log(`SandboxStatus for ${server.name}:`, {
        type: server.type,
        status: server.status,
        sandboxUrl: server.sandboxUrl,
        isVisible
    });

    React.useEffect(() => {
        if (server.type !== 'stdio' || !isVisible) {
            console.log(`SandboxStatus effect skipped for ${server.name}:`, {
                isStdio: server.type === 'stdio',
                isVisible
            });
            return;
        }

        const checkSandboxStatus = async () => {
            try {
                console.log(`Checking sandbox status for ${server.name}...`);
                const status = await getSandboxStatus(server.id);
                console.log(`Sandbox status for ${server.name}:`, status);
                
                if (!status.exists) {
                    // Sandbox doesn't exist anymore, show appropriate state
                    setSandboxState('not found');
                    console.log(`Sandbox ${server.id} no longer exists, cleaning up server...`);
                    await stopServer(server.id);
                    return;
                }

                if (status.state) {
                    setSandboxState(status.state);
                    
                    // Only auto-cleanup if server has been running for more than 30 seconds
                    // This prevents race conditions during startup
                    const serverStartTime = server.sandboxStartTime || 0;
                    const timeSinceStart = Date.now() - serverStartTime;
                    const gracePeriod = 30000; // 30 seconds
                    
                    if (timeSinceStart > gracePeriod && 
                        (status.state.toLowerCase() === 'stopped' || status.state.toLowerCase() === 'archived')) {
                        console.log(`Sandbox ${server.id} is ${status.state} after grace period, cleaning up server...`);
                        await stopServer(server.id);
                        return;
                    }
                } else {
                    // Status exists but no state - show as unknown
                    setSandboxState('unknown');
                }
            } catch (error) {
                console.error(`Error checking sandbox status for ${server.id}:`, error);
                setSandboxState('error');
            }
        };

        // Check sandbox status every 10 seconds
        const statusInterval = setInterval(checkSandboxStatus, 10000);

        return () => {
            clearInterval(statusInterval);
        };
    }, [server.type, server.id, stopServer, isVisible]);

    if (server.type !== 'stdio') {
        console.log(`SandboxStatus not rendering for ${server.name}: not a stdio server`);
        return null;
    }

    console.log(`SandboxStatus rendering for ${server.name} with state:`, sandboxState);

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium font-mono ${
                    sandboxState.toLowerCase() === 'started' ? 'bg-primary/10 text-primary border-primary/20' : 
                    sandboxState.toLowerCase() === 'stopped' ? 'bg-destructive/10 text-destructive border-destructive/20' : 
                    sandboxState.toLowerCase() === 'not found' ? 'bg-warning/10 text-warning border-warning/20' : 
                    sandboxState.toLowerCase() === 'unknown' ? 'bg-warning/10 text-warning border-warning/20' : 
                    sandboxState.toLowerCase() === 'error' ? 'bg-destructive/10 text-destructive border-destructive/20' : 
                    'bg-secondary/10 text-secondary border-secondary/20'
                }`}>
                    <span>{sandboxState || 'checking...'}</span>
                </div>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
                <p>Sandbox status • Auto-stops after 24hr inactivity</p>
            </TooltipContent>
        </Tooltip>
    );
};

// Add near the top after existing interfaces
interface FormErrors {
    name?: string;
    url?: string;
    command?: string;
    args?: string;
}

export const MCPServerManager = ({
    servers,
    onServersChange,
    selectedServers,
    onSelectedServersChange,
    open,
    onOpenChange
}: MCPServerManagerProps) => {
    const [newServer, setNewServer] = useState<Omit<MCPServer, 'id'>>(INITIAL_NEW_SERVER);
    const [view, setView] = useState<'list' | 'add' | 'import'>('list');
    const [jsonConfig, setJsonConfig] = useState('');
    const [newEnvVar, setNewEnvVar] = useState<KeyValuePair>({ key: '', value: '' });
    const [newHeader, setNewHeader] = useState<KeyValuePair>({ key: '', value: '' });
    const [editingServerId, setEditingServerId] = useState<string | null>(null);
    const [showSensitiveEnvValues, setShowSensitiveEnvValues] = useState<Record<number, boolean>>({});
    const [showSensitiveHeaderValues, setShowSensitiveHeaderValues] = useState<Record<number, boolean>>({});
    const [editingEnvIndex, setEditingEnvIndex] = useState<number | null>(null);
    const [editingHeaderIndex, setEditingHeaderIndex] = useState<number | null>(null);
    const [editedEnvValue, setEditedEnvValue] = useState<string>('');
    const [editedHeaderValue, setEditedHeaderValue] = useState<string>('');
    const [search, setSearch] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [isCleaningUp, setIsCleaningUp] = useState(false);
    // Add new state for better UX
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [isValidating, setIsValidating] = useState(false);
    const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
    
    // Add access to the MCP context for server control
    const { startServer, stopServer, updateServerStatus } = useMCP();

    // Real-time form validation
    const validateForm = (server: Omit<MCPServer, 'id'>): FormErrors => {
        const errors: FormErrors = {};
        
        if (!server.name.trim()) {
            errors.name = "Server name is required";
        } else if (server.name.length < 3) {
            errors.name = "Server name must be at least 3 characters";
        }
        
        if (server.type === 'sse') {
            if (!server.url.trim()) {
                errors.url = "Server URL is required for SSE transport";
            } else {
                try {
                    new URL(server.url);
                } catch {
                    errors.url = "Please enter a valid URL";
                }
            }
        }
        
        if (server.type === 'stdio') {
            if (!server.command || !server.command.trim()) {
                errors.command = "Command is required for stdio transport";
            }
            if (!server.args?.length) {
                errors.args = "At least one argument is required";
            }
        }
        
        return errors;
    };

    // Update form validation on changes
    React.useEffect(() => {
        if (view === 'add') {
            const errors = validateForm(newServer);
            setFormErrors(errors);
        }
    }, [newServer, view]);

    const resetAndClose = () => {
        setView('list');
        setNewServer(INITIAL_NEW_SERVER);
        setJsonConfig('');
        setNewEnvVar({ key: '', value: '' });
        setNewHeader({ key: '', value: '' });
        setShowSensitiveEnvValues({});
        setShowSensitiveHeaderValues({});
        setEditingEnvIndex(null);
        setEditingHeaderIndex(null);
        onOpenChange(false);
    };

    const importFromJson = () => {
        try {
            const parsedServers = parseClaudeDesktopConfig(jsonConfig);
            
            if (parsedServers.length === 0) {
                toast.error("No servers found in configuration");
                return;
            }
            
            // Check for duplicates and filter out existing servers
            const newServers: MCPServer[] = [];
            const skippedServers: string[] = [];
            
            parsedServers.forEach(server => {
                const existingServer = checkForDuplicates(server);
                if (existingServer) {
                    skippedServers.push(server.name || 'Unnamed server');
                } else {
                    newServers.push({
                        ...server,
                        id: crypto.randomUUID()
                    });
                }
            });
            
            if (newServers.length === 0) {
                toast.error("All servers already exist. No new servers imported.");
                return;
            }
            
            // Add new servers
            const updatedServers = [...servers, ...newServers];
            onServersChange(updatedServers);
            
            // Show summary message
            let message = `Imported ${newServers.length} server${newServers.length !== 1 ? 's' : ''}`;
            if (skippedServers.length > 0) {
                message += `. Skipped ${skippedServers.length} duplicate${skippedServers.length !== 1 ? 's' : ''}: ${skippedServers.join(', ')}`;
            }
            
            toast.success(message);
            setView('list');
            setJsonConfig('');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to import configuration');
        }
    };

    const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check if it's a JSON file
        if (!file.name.toLowerCase().endsWith('.json')) {
            toast.error("Please select a JSON file");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            if (content) {
                setJsonConfig(content);
                toast.success("File loaded successfully");
            }
        };
        reader.onerror = () => {
            toast.error("Failed to read file");
        };
        reader.readAsText(file);
        
        // Reset the input so the same file can be selected again
        event.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.name.toLowerCase().endsWith('.json')) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const content = event.target?.result as string;
                    if (content) {
                        setJsonConfig(content);
                        toast.success(`Loaded ${file.name}`);
                    }
                };
                reader.readAsText(file);
            } else {
                toast.error("Please drop a JSON file");
            }
        }
    };

    const addServer = () => {
        if (!newServer.name) {
            toast.error("Server name is required");
            return;
        }

        if (newServer.type === 'sse' && !newServer.url) {
            toast.error("Server URL is required for SSE transport");
            return;
        }

        if (newServer.type === 'stdio' && (!newServer.command || !newServer.args?.length)) {
            toast.error("Command and at least one argument are required for stdio transport");
            return;
        }

        // Check for duplicates
        const existingServer = checkForDuplicates(newServer);
        if (existingServer) {
            let duplicateMessage = "";
            if (existingServer.name.toLowerCase() === newServer.name.toLowerCase()) {
                duplicateMessage = `A server named "${existingServer.name}" already exists`;
            } else if (newServer.type === 'sse' && existingServer.url === newServer.url) {
                duplicateMessage = `A server with URL "${newServer.url}" already exists`;
            } else if (newServer.type === 'stdio') {
                duplicateMessage = `A server with this command configuration already exists`;
            }
            
            toast.error(duplicateMessage + ". Try editing the existing server instead.");
            return;
        }

        const id = crypto.randomUUID();
        const updatedServers = [...servers, { ...newServer, id }];
        onServersChange(updatedServers);

        toast.success(`Added MCP server: ${newServer.name}`);
        setView('list');
        setNewServer(INITIAL_NEW_SERVER);
        setNewEnvVar({ key: '', value: '' });
        setNewHeader({ key: '', value: '' });
        setShowSensitiveEnvValues({});
        setShowSensitiveHeaderValues({});
    };

    const removeServer = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const updatedServers = servers.filter(server => server.id !== id);
        onServersChange(updatedServers);

        // If the removed server was selected, remove it from selected servers
        if (selectedServers.includes(id)) {
            onSelectedServersChange(selectedServers.filter(serverId => serverId !== id));
        }

        toast.success("Server removed");
    };

    const toggleServer = (id: string) => {
        if (selectedServers.includes(id)) {
            // Remove from selected servers but DON'T stop the server
            onSelectedServersChange(selectedServers.filter(serverId => serverId !== id));
            const server = servers.find(s => s.id === id);
            
            if (server) {
                toast.success(`Disabled MCP server: ${server.name}`);
            }
        } else {
            // Add to selected servers but DON'T auto-start
            onSelectedServersChange([...selectedServers, id]);
            const server = servers.find(s => s.id === id);
            
            if (server) {
                toast.success(`Enabled MCP server: ${server.name}`);
            }
        }
    };

    const deleteAllServers = async () => {
        if (servers.length === 0) return;
        
        try {
            console.log('Starting delete all servers operation');
            console.log('Current servers count:', servers.length);
            
            // First, cleanup all sandboxes
            try {
                const { stopped, errors } = await stopAllSandboxes();
                if (stopped > 0) {
                    console.log(`Cleaned up ${stopped} sandboxes during server deletion`);
                }
                if (errors > 0) {
                    console.warn(`${errors} sandboxes had errors during cleanup`);
                }
            } catch (error) {
                console.error('Error cleaning up sandboxes during server deletion:', error);
                // Continue with server deletion even if sandbox cleanup fails
            }
            
            // Clear selected servers first (if any)
            if (selectedServers.length > 0) {
                onSelectedServersChange([]);
            }
            
            // Clear all servers immediately
            onServersChange([]);
          
            
            toast.success("All servers and sandboxes deleted permanently");
            console.log('All servers deleted');
            
        } catch (error) {
            console.error('Error deleting servers:', error);
            toast.error("Failed to delete servers");
        }
    };

    const exportServers = () => {
        if (servers.length === 0) {
            toast.error("No servers to export");
            return;
        }

        // Convert servers to export format (remove IDs and status info)
        const exportData = servers.map(server => ({
            name: server.name,
            url: server.url,
            type: server.type,
            command: server.command,
            args: server.args,
            env: server.env || [],
            headers: server.headers || []
        }));

        // Create and download the JSON file
        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `archie-mcp-servers-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success(`Exported ${servers.length} server${servers.length !== 1 ? 's' : ''} to JSON file`);
    };

    const cleanupSandboxes = async () => {
        setIsCleaningUp(true);
        try {
            // First, get the count of active sandboxes
            const { count, ids } = await listActiveSandboxes();
            
            if (count === 0) {
                toast.info("No active sandboxes to cleanup");
                return;
            }

            // Stop all sandboxes
            const { stopped, errors } = await stopAllSandboxes();
            
            if (errors > 0) {
                toast.warning(`Cleaned up ${stopped} sandboxes, but ${errors} had errors`);
            } else {
                toast.success(`Successfully cleaned up ${stopped} sandbox${stopped !== 1 ? 'es' : ''}`);
            }
        } catch (error) {
            console.error('Error cleaning up sandboxes:', error);
            toast.error(`Failed to cleanup sandboxes: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsCleaningUp(false);
        }
    };

    const handleArgsChange = (value: string) => {
        try {
            // Try to parse as JSON if it starts with [ (array)
            const argsArray = value.trim().startsWith('[')
                ? JSON.parse(value)
                : value.split(' ').filter(Boolean);

            setNewServer({ ...newServer, args: argsArray });
        } catch (error) {
            // If parsing fails, just split by spaces
            setNewServer({ ...newServer, args: value.split(' ').filter(Boolean) });
        }
    };

    const addEnvVar = () => {
        if (!newEnvVar.key) return;

        setNewServer({
            ...newServer,
            env: [...(newServer.env || []), { ...newEnvVar }]
        });

        setNewEnvVar({ key: '', value: '' });
    };

    const removeEnvVar = (index: number) => {
        const updatedEnv = [...(newServer.env || [])];
        updatedEnv.splice(index, 1);
        setNewServer({ ...newServer, env: updatedEnv });
        
        // Clean up visibility state for this index
        const updatedVisibility = { ...showSensitiveEnvValues };
        delete updatedVisibility[index];
        setShowSensitiveEnvValues(updatedVisibility);
        
        // If currently editing this value, cancel editing
        if (editingEnvIndex === index) {
            setEditingEnvIndex(null);
        }
    };

    const startEditEnvValue = (index: number, value: string) => {
        setEditingEnvIndex(index);
        setEditedEnvValue(value);
    };

    const saveEditedEnvValue = () => {
        if (editingEnvIndex !== null) {
            const updatedEnv = [...(newServer.env || [])];
            updatedEnv[editingEnvIndex] = {
                ...updatedEnv[editingEnvIndex],
                value: editedEnvValue
            };
            setNewServer({ ...newServer, env: updatedEnv });
            setEditingEnvIndex(null);
        }
    };

    const addHeader = () => {
        if (!newHeader.key) return;

        setNewServer({
            ...newServer,
            headers: [...(newServer.headers || []), { ...newHeader }]
        });

        setNewHeader({ key: '', value: '' });
    };

    const removeHeader = (index: number) => {
        const updatedHeaders = [...(newServer.headers || [])];
        updatedHeaders.splice(index, 1);
        setNewServer({ ...newServer, headers: updatedHeaders });
        
        // Clean up visibility state for this index
        const updatedVisibility = { ...showSensitiveHeaderValues };
        delete updatedVisibility[index];
        setShowSensitiveHeaderValues(updatedVisibility);
        
        // If currently editing this value, cancel editing
        if (editingHeaderIndex === index) {
            setEditingHeaderIndex(null);
        }
    };

    const startEditHeaderValue = (index: number, value: string) => {
        setEditingHeaderIndex(index);
        setEditedHeaderValue(value);
    };

    const saveEditedHeaderValue = () => {
        if (editingHeaderIndex !== null) {
            const updatedHeaders = [...(newServer.headers || [])];
            updatedHeaders[editingHeaderIndex] = {
                ...updatedHeaders[editingHeaderIndex],
                value: editedHeaderValue
            };
            setNewServer({ ...newServer, headers: updatedHeaders });
            setEditingHeaderIndex(null);
        }
    };

    const toggleSensitiveEnvValue = (index: number) => {
        setShowSensitiveEnvValues(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const toggleSensitiveHeaderValue = (index: number) => {
        setShowSensitiveHeaderValues(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const hasAdvancedConfig = (server: MCPServer) => {
        return (server.env && server.env.length > 0) ||
            (server.headers && server.headers.length > 0);
    };

    // Editing support
    const startEditing = (server: MCPServer) => {
        setEditingServerId(server.id);
        setNewServer({
            name: server.name,
            url: server.url,
            type: server.type,
            command: server.command,
            args: server.args,
            env: server.env,
            headers: server.headers
        });
        setView('add');
        // Reset sensitive value visibility states
        setShowSensitiveEnvValues({});
        setShowSensitiveHeaderValues({});
        setEditingEnvIndex(null);
        setEditingHeaderIndex(null);
    };

    const handleFormCancel = () => {
        if (view === 'add') {
            setView('list');
            setEditingServerId(null);
            setNewServer(INITIAL_NEW_SERVER);
            setShowSensitiveEnvValues({});
            setShowSensitiveHeaderValues({});
            setEditingEnvIndex(null);
            setEditingHeaderIndex(null);
        } else if (view === 'import') {
            setView('list');
            setJsonConfig('');
        } else {
            resetAndClose();
        }
    };

    const updateServer = () => {
        if (!newServer.name) {
            toast.error("Server name is required");
            return;
        }
        if (newServer.type === 'sse' && !newServer.url) {
            toast.error("Server URL is required for SSE transport");
            return;
        }
        if (newServer.type === 'stdio' && (!newServer.command || !newServer.args?.length)) {
            toast.error("Command and at least one argument are required for stdio transport");
            return;
        }

        // Check for duplicates (excluding the current server being edited)
        const existingServer = checkForDuplicates(newServer, editingServerId!);
        if (existingServer) {
            let duplicateMessage = "";
            if (existingServer.name.toLowerCase() === newServer.name.toLowerCase()) {
                duplicateMessage = `A server named "${existingServer.name}" already exists`;
            } else if (newServer.type === 'sse' && existingServer.url === newServer.url) {
                duplicateMessage = `A server with URL "${newServer.url}" already exists`;
            } else if (newServer.type === 'stdio') {
                duplicateMessage = `A server with this command configuration already exists`;
            }
            
            toast.error(duplicateMessage + ". Choose a different configuration.");
            return;
        }

        const updated = servers.map(s =>
            s.id === editingServerId ? { ...newServer, id: editingServerId! } : s
        );
        onServersChange(updated);
        toast.success(`Updated MCP server: ${newServer.name}`);
        setView('list');
        setEditingServerId(null);
        setNewServer(INITIAL_NEW_SERVER);
        setShowSensitiveEnvValues({});
        setShowSensitiveHeaderValues({});
    };

    // Update functions to control servers
    const toggleServerStatus = async (server: MCPServer, e: React.MouseEvent) => {
        e.stopPropagation();
        
        if (!server.status || server.status === 'disconnected' || server.status === 'error') {
            try {
                updateServerStatus(server.id, 'connecting');
                const success = await startServer(server.id);
                
                if (success) {
                    updateServerStatus(server.id, 'connected');
                    toast.success(`Started server: ${server.name}`);
                } else {
                    updateServerStatus(server.id, 'error', 'Failed to start server');
                    toast.error(`Failed to start server: ${server.name}`);
                }
            } catch (error) {
                updateServerStatus(server.id, 'error', 
                    `Error: ${error instanceof Error ? error.message : String(error)}`);
                toast.error(`Error starting server: ${error instanceof Error ? error.message : String(error)}`);
            }
        } else {
            try {
                // Show different message for stdio servers with sandboxes
                const hasActiveSandbox = server.type === 'stdio' && server.sandboxUrl;
                if (hasActiveSandbox) {
                    toast.info(`Stopping server and sandbox: ${server.name}...`);
                }
                
                const success = await stopServer(server.id);
                if (success) {
                    updateServerStatus(server.id, 'disconnected');
                    if (hasActiveSandbox) {
                        toast.success(`Stopped server and sandbox: ${server.name}`);
                    } else {
                    toast.success(`Stopped server: ${server.name}`);
                    }
                } else {
                    toast.error(`Failed to stop server: ${server.name}`);
                }
            } catch (error) {
                toast.error(`Error stopping server: ${error instanceof Error ? error.message : String(error)}`);
            }
        }
    };

    // Update function to restart a server
    const restartServer = async (server: MCPServer, e: React.MouseEvent) => {
        e.stopPropagation();
        
        try {
            const hasActiveSandbox = server.type === 'stdio' && server.sandboxUrl;
            if (hasActiveSandbox) {
                toast.info(`Restarting server and sandbox: ${server.name}...`);
            }
            
            // First stop it
            if (server.status === 'connected' || server.status === 'connecting') {
                await stopServer(server.id);
                updateServerStatus(server.id, 'disconnected');
            }
            
            // Then start it again (with delay to ensure proper cleanup)
            setTimeout(async () => {
                updateServerStatus(server.id, 'connecting');
                const success = await startServer(server.id);
                
                if (success) {
                    updateServerStatus(server.id, 'connected');
                    if (hasActiveSandbox) {
                        toast.success(`Restarted server and sandbox: ${server.name}`);
                    } else {
                    toast.success(`Restarted server: ${server.name}`);
                    }
                } else {
                    updateServerStatus(server.id, 'error', 'Failed to restart server');
                    toast.error(`Failed to restart server: ${server.name}`);
                }
            }, 500);
        } catch (error) {
            updateServerStatus(server.id, 'error', 
                `Error: ${error instanceof Error ? error.message : String(error)}`);
            toast.error(`Error restarting server: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    // UI element to display the correct server URL
    const getServerDisplayUrl = (server: MCPServer): string => {
        // Always show the configured URL or command, not the sandbox URL
        return server.type === 'sse' 
            ? server.url 
            : `${server.command} ${server.args?.join(' ')}`;
    };

    // Update the hover info function to return richer content
    const getServerStatusHoverInfo = (server: MCPServer): string | undefined => {
        // For connected stdio servers, show the sandbox URL as hover info
        if (server.type === 'stdio' && server.status === 'connected' && server.sandboxUrl) {
            return `Running at: ${server.sandboxUrl}`;
        }
        
        // For error status, show the error message
        if (server.status === 'error' && server.errorMessage) {
            return `Error: ${server.errorMessage}`;
        }
        
        return undefined;
    };

    // Filter servers based on search
    const filteredServers = servers.filter(server =>
        server.name.toLowerCase().includes(search.toLowerCase()) ||
        server.url.toLowerCase().includes(search.toLowerCase()) ||
        (server.command || '').toLowerCase().includes(search.toLowerCase())
    );

    // Check for duplicate servers
    const checkForDuplicates = (serverToCheck: Omit<MCPServer, 'id'>, excludeId?: string) => {
        const duplicate = servers.find(server => {
            if (excludeId && server.id === excludeId) return false;
            
            // Check for exact name match
            if (server.name.toLowerCase() === serverToCheck.name.toLowerCase()) {
                return true;
            }
            
            // For SSE servers, check URL
            if (serverToCheck.type === 'sse' && server.type === 'sse' && 
                server.url && serverToCheck.url && 
                server.url.toLowerCase() === serverToCheck.url.toLowerCase()) {
                return true;
            }
            
            // For stdio servers, check command + args combination
            if (serverToCheck.type === 'stdio' && server.type === 'stdio' &&
                server.command === serverToCheck.command &&
                JSON.stringify(server.args) === JSON.stringify(serverToCheck.args)) {
                return true;
            }
            
            return false;
        });
        
        return duplicate;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[900px] max-h-[90vh] w-[95vw] overflow-hidden flex flex-col">
                <DialogHeader className="pb-2">
                    <DialogTitle className="flex items-center gap-2 text-lg">
                        <Server className="h-5 w-5 text-primary" />
                        MCP Server Configuration
                        {view !== 'list' && (
                            <Badge variant="outline" className="ml-2 text-xs">
                                {view === 'add' ? (editingServerId ? 'Editing' : 'Adding New') : 'Import'}
                            </Badge>
                        )}
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        {view === 'list' ? (
                            <>
                                Connect to Model Context Protocol servers to access additional AI tools.
                                {selectedServers.length > 0 && (
                                    <span className="block mt-1 text-xs font-medium text-primary">
                                        {selectedServers.length} server{selectedServers.length !== 1 ? 's' : ''} currently active
                                    </span>
                                )}
                            </>
                        ) : view === 'add' ? (
                            editingServerId ? "Modify your server configuration below." : "Add a new MCP server to extend your AI capabilities."
                        ) : (
                            "Import servers from JSON configuration files or raw URLs."
                        )}
                    </DialogDescription>
                </DialogHeader>

                {/* Progress indicator for multi-step process */}
                {view !== 'list' && (
                    <div className="flex items-center gap-2 py-2 px-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setView('list')}
                                className="h-6 px-2 text-xs hover:bg-muted/50 hover:text-foreground transition-colors"
                            >
                                ← Back to Servers
                            </Button>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-primary font-medium">
                                {view === 'add' ? (editingServerId ? 'Edit Server' : 'Add Server') : 'Import Servers'}
                            </span>
                        </div>
                    </div>
                )}

                {view === 'list' ? (
                    <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                        {servers.length > 0 ? (
                            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                    <h3 className="text-sm font-medium">Available Servers ({servers.length})</h3>
                                    <span className="text-xs text-muted-foreground hidden sm:block">
                                        Select multiple servers to combine their tools
                                    </span>
                                </div>
                                
                                <div className="mb-4 flex-shrink-0">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                        <Input
                                            placeholder="Search servers by name, URL, or command..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            className="pl-9"
                                        />
                                        {search && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setSearch('')}
                                                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                                            >
                                                <X className="h-3 w-3" />
                                            </Button>
                                        )}
                                    </div>
                                    {search && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {filteredServers.length} of {servers.length} servers match your search
                                        </p>
                                    )}
                                </div>
                                
                                <div className="overflow-y-auto pr-1 flex-1 gap-3 flex flex-col pb-16 min-h-0">
                                    {filteredServers
                                        .sort((a, b) => {
                                            // Sort by status first, then by selection
                                            const aConnected = a.status === 'connected';
                                            const bConnected = b.status === 'connected';
                                            if (aConnected && !bConnected) return -1;
                                            if (!aConnected && bConnected) return 1;
                                            
                                            const aActive = selectedServers.includes(a.id);
                                            const bActive = selectedServers.includes(b.id);
                                            if (aActive && !bActive) return -1;
                                            if (!aActive && bActive) return 1;
                                            return a.name.localeCompare(b.name);
                                        })
                                        .map((server) => {
                                        const isActive = selectedServers.includes(server.id);
                                        const isRunning = server.status === 'connected' || server.status === 'connecting';
                                        const hasError = server.status === 'error';

                                        return (
                                            <div
                                                key={server.id}
                                                className={`
                                                    relative flex flex-col p-4 rounded-xl transition-all duration-200 group
                                                    border-2 ${
                                                        server.status === 'connected'
                                                            ? 'border-success/30 bg-success/10 shadow-md'
                                                            : hasError
                                                                ? 'border-destructive/30 bg-destructive/10'
                                                                : isActive
                                                                    ? 'border-primary bg-primary/5 shadow-md'
                                                                    : 'border-border hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm'
                                                    }
                                                `}
                                            >
                                                {/* Server Header with improved visual hierarchy */}
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <div className={`
                                                            p-2.5 rounded-lg transition-colors
                                                            ${server.status === 'connected' 
                                                                ? 'bg-success/20' 
                                                                : hasError
                                                                    ? 'bg-destructive/20'
                                                                    : isActive ? 'bg-primary/10' : 'bg-muted'
                                                            }
                                                        `}>
                                                            {server.type === 'sse' ? (
                                                                <Globe className={`h-4 w-4 ${
                                                                    server.status === 'connected' 
                                                                        ? 'text-success' 
                                                                        : hasError
                                                                            ? 'text-destructive'
                                                                            : isActive ? 'text-primary' : 'text-muted-foreground'
                                                                }`} />
                                                            ) : (
                                                                <Terminal className={`h-4 w-4 ${
                                                                    server.status === 'connected' 
                                                                        ? 'text-success' 
                                                                        : hasError
                                                                            ? 'text-destructive'
                                                                            : isActive ? 'text-primary' : 'text-muted-foreground'
                                                                }`} />
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1.5">
                                                                <h4 className="text-sm font-semibold truncate">{server.name}</h4>
                                                                <Badge 
                                                                    variant={server.type === 'sse' ? 'default' : 'secondary'} 
                                                                    className="text-xs font-mono"
                                                                >
                                                                    {server.type.toUpperCase()}
                                                                </Badge>
                                                                {hasAdvancedConfig(server) && (
                                                                    <TooltipProvider>
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <Badge variant="outline" className="text-xs">
                                                                                    <Cog className="h-3 w-3" />
                                                                                </Badge>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent>
                                                                                Has environment variables or headers configured
                                                                            </TooltipContent>
                                                                        </Tooltip>
                                                                    </TooltipProvider>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-muted-foreground truncate leading-relaxed">
                                                                {getServerDisplayUrl(server)}
                                                            </p>
                                                            {/* Status indicator with better visibility */}
                                                            <div className="flex items-center gap-2 mt-1.5">
                                                                <StatusIndicator 
                                                                    status={server.status} 
                                                                    hoverInfo={getServerStatusHoverInfo(server)}
                                                                />
                                                                <SandboxStatus server={server} isVisible={open} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <Separator className="mb-3" />

                                                {/* Server Controls with improved layout */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-1">
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={(e) => toggleServerStatus(server, e)}
                                                                        className="h-8 w-8 p-0"
                                                                        disabled={server.status === 'connecting'}
                                                                    >
                                                                        {isRunning ? (
                                                                            <Square className="h-4 w-4 text-destructive" />
                                                                        ) : (
                                                                            <Play className="h-4 w-4 text-success" />
                                                                        )}
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {isRunning ? "Stop server" : "Start server"}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                        
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={(e) => restartServer(server, e)}
                                                                        className="h-8 w-8 p-0"
                                                                        disabled={server.status === 'connecting'}
                                                                    >
                                                                        <RefreshCw className="h-4 w-4 text-muted-foreground" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    Restart server
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                        
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => startEditing(server)}
                                                                        className="h-8 w-8 p-0"
                                                                    >
                                                                        <Edit2 className="h-4 w-4 text-muted-foreground" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    Edit server configuration
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                        
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={(e) => removeServer(server.id, e)}
                                                                        className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    Remove server
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>

                                                    {/* Improved action button */}
                                                    <Button
                                                        size="sm"
                                                        className={`gap-2 min-w-[110px] transition-all ${
                                                            server.status === 'connecting' ? 'cursor-not-allowed' : ''
                                                        }`}
                                                        variant={server.status === 'connected' ? "default" : hasError ? "destructive" : "outline"}
                                                        onClick={(e) => toggleServerStatus(server, e)}
                                                        disabled={server.status === 'connecting'}
                                                    >
                                                        {server.status === 'connected' ? (
                                                            <>
                                                                <Square className="h-4 w-4" />
                                                                Stop Server
                                                            </>
                                                        ) : server.status === 'connecting' ? (
                                                            <>
                                                                <RefreshCw className="h-4 w-4 animate-spin" />
                                                                Connecting...
                                                            </>
                                                        ) : server.status === 'error' ? (
                                                            <>
                                                                <AlertTriangle className="h-4 w-4" />
                                                                Retry
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Play className="h-4 w-4" />
                                                                Start Server
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {/* Improved empty search state */}
                                    {search && filteredServers.length === 0 && (
                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="rounded-full p-3 bg-muted/50 mb-3">
                                                <Search className="h-6 w-6 text-muted-foreground" />
                                            </div>
                                            <h3 className="text-sm font-medium mb-1">No servers found</h3>
                                            <p className="text-xs text-muted-foreground mb-3 max-w-[250px]">
                                                No servers match "{search}". Try a different search term.
                                            </p>
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                onClick={() => setSearch('')}
                                                className="text-xs"
                                            >
                                                Clear search
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Improved empty state
                            <div className="flex-1 py-8 pb-16 flex flex-col items-center justify-center space-y-6">
                                <div className="rounded-full p-4 bg-primary/10">
                                    <Server className="h-8 w-8 text-primary" />
                                </div>
                                <div className="text-center space-y-2 max-w-[320px]">
                                    <h3 className="text-lg font-semibold">No MCP Servers</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        Add your first MCP server to access additional AI tools and extend your capabilities.
                                    </p>
                                </div>
                                
                                <div className="flex flex-col sm:flex-row items-center gap-3">
                                    <Button onClick={() => setView('add')} className="gap-2">
                                        <PlusCircle className="h-4 w-4" />
                                        Add Your First Server
                                    </Button>
                                    <Button variant="outline" onClick={() => setView('import')} className="gap-2">
                                        <Upload className="h-4 w-4" />
                                        Import from Config
                                    </Button>
                                </div>
                                
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-2">
                                    <a
                                        href="https://modelcontextprotocol.io"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 hover:text-primary transition-colors"
                                    >
                                        Learn about MCP
                                        <ExternalLink className="h-3 w-3" />
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                ) : view === 'import' ? (
                    <div className="space-y-4 overflow-y-auto px-1 py-0.5 mb-14 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        <h3 className="text-sm font-medium">Import from JSON Configuration</h3>
                        <div className="space-y-4">
                            <div className="text-xs text-muted-foreground">
                                <p>Choose a format below and copy the example, or paste your existing configuration:</p>
                            </div>
                            
                            <Tabs defaultValue="url" className="w-full">
                                <TabsList className="grid w-full grid-cols-5">
                                    <TabsTrigger value="url" className="text-xs">Raw URL</TabsTrigger>
                                    <TabsTrigger value="claude" className="text-xs">Claude Desktop</TabsTrigger>
                                    <TabsTrigger value="array" className="text-xs">Array</TabsTrigger>
                                    <TabsTrigger value="single" className="text-xs">Single</TabsTrigger>
                                    <TabsTrigger value="sse" className="text-xs">SSE</TabsTrigger>
                                </TabsList>
                                
                                <TabsContent value="url" className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-medium">Paste URL Directly</p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                            onClick={() => {
                                                const example = `https://server.smithery.ai/exa/mcp?api_key=your-api-key-here`;
                                                navigator.clipboard.writeText(example);
                                                toast.success("Example URL copied to clipboard");
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                    <div className="bg-muted/50 p-3 rounded text-xs font-mono overflow-x-auto border">
                                        {`https://server.smithery.ai/exa/mcp?api_key=your-api-key-here`}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Just paste any MCP server URL directly. Works with Smithery URLs and other SSE endpoints.
                                    </p>
                                </TabsContent>
                                
                                <TabsContent value="claude" className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-medium">Claude Desktop Configuration</p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                            onClick={() => {
                                                const example = `{
  "mcpServers": {
    "exa": {
      "command": "npx",
      "args": ["-y", "@smithery/cli@latest", "run", "exa", "--key", "..."],
      "env": {
        "API_KEY": "your-key-here"
      }
    },
    "filesystem": {
      "command": "node",
      "args": ["path/to/filesystem-server.js"]
    }
  }
}`;
                                                navigator.clipboard.writeText(example);
                                                toast.success("Example copied to clipboard");
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                    <div className="bg-muted/50 p-3 rounded text-xs font-mono overflow-x-auto border">
                                        {`{
  "mcpServers": {
    "exa": {
      "command": "npx",
      "args": ["-y", "@smithery/cli@latest", "run", "exa", "--key", "..."],
      "env": {
        "API_KEY": "your-key-here"
      }
    },
    "filesystem": {
      "command": "node",
      "args": ["path/to/filesystem-server.js"]
    }
  }
}`}
                                    </div>
                                </TabsContent>
                                
                                <TabsContent value="array" className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-medium">Server Array Format</p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                            onClick={() => {
                                                const example = `[
  {
    "name": "Exa Search",
    "type": "stdio",
    "command": "npx",
    "args": ["-y", "@smithery/cli@latest", "run", "exa"],
    "env": [
      {"key": "API_KEY", "value": "your-key-here"}
    ]
  },
  {
    "name": "Web Server", 
    "type": "sse",
    "url": "https://api.example.com/mcp/sse",
    "headers": [
      {"key": "Authorization", "value": "Bearer token123"}
    ]
  }
]`;
                                                navigator.clipboard.writeText(example);
                                                toast.success("Example copied to clipboard");
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                    <div className="bg-muted/50 p-3 rounded text-xs font-mono overflow-x-auto border">
                                        {`[
  {
    "name": "Exa Search",
    "type": "stdio", 
    "command": "npx",
    "args": ["-y", "@smithery/cli@latest", "run", "exa"],
    "env": [
      {"key": "API_KEY", "value": "your-key-here"}
    ]
  },
  {
    "name": "Web Server",
    "type": "sse",
    "url": "https://api.example.com/mcp/sse", 
    "headers": [
      {"key": "Authorization", "value": "Bearer token123"}
    ]
  }
]`}
                                    </div>
                                </TabsContent>
                                
                                <TabsContent value="single" className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-medium">Single Server Format</p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                            onClick={() => {
                                                const example = `{
  "name": "My MCP Server",
  "type": "stdio",
  "command": "python",
  "args": ["server.py", "--port", "3000"],
  "env": [
    {"key": "DATABASE_URL", "value": "sqlite:///data.db"},
    {"key": "LOG_LEVEL", "value": "info"}
  ],
  "headers": []
}`;
                                                navigator.clipboard.writeText(example);
                                                toast.success("Example copied to clipboard");
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                    <div className="bg-muted/50 p-3 rounded text-xs font-mono overflow-x-auto border">
                                        {`{
  "name": "My MCP Server",
  "type": "stdio",
  "command": "python", 
  "args": ["server.py", "--port", "3000"],
  "env": [
    {"key": "DATABASE_URL", "value": "sqlite:///data.db"},
    {"key": "LOG_LEVEL", "value": "info"}
  ],
  "headers": []
}`}
                                    </div>
                                </TabsContent>
                                
                                <TabsContent value="sse" className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-medium">SSE Server Format</p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                            onClick={() => {
                                                const example = `{
  "name": "Remote MCP API",
  "type": "sse",
  "url": "https://mcp-server.example.com/sse",
  "headers": [
    {"key": "Authorization", "value": "Bearer your-token"},
    {"key": "X-API-Version", "value": "v1"}
  ],
  "env": []
}`;
                                                navigator.clipboard.writeText(example);
                                                toast.success("Example copied to clipboard");
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                    <div className="bg-muted/50 p-3 rounded text-xs font-mono overflow-x-auto border">
                                        {`{
  "name": "Remote MCP API",
  "type": "sse",
  "url": "https://mcp-server.example.com/sse",
  "headers": [
    {"key": "Authorization", "value": "Bearer your-token"},
    {"key": "X-API-Version", "value": "v1"}
  ],
  "env": []
}`}
                                    </div>
                                </TabsContent>
                            </Tabs>
                            
                            <div className="grid gap-1.5">
                                <Label htmlFor="json-config">Your Configuration</Label>
                                <div className="space-y-4">
                                    {/* Drag and Drop Upload Area */}
                                    <div 
                                        className={`
                                            relative border-2 border-dashed rounded-lg p-6 text-center transition-all
                                            ${isDragOver 
                                                ? 'border-primary bg-primary/5 scale-[1.02]' 
                                                : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-primary/5'
                                            }
                                        `}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            id="file-import"
                                            type="file"
                                            accept=".json,application/json"
                                            onChange={handleFileImport}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="flex flex-col items-center gap-2">
                                            <div className={`p-3 rounded-full ${isDragOver ? 'bg-primary/20' : 'bg-muted'}`}>
                                                <FileJson className={`h-6 w-6 ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`} />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-sm font-medium">
                                                    {isDragOver ? 'Drop your JSON file here' : 'Drop JSON file or click to browse'}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    Upload exported server configurations
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="relative">
                                        <Separator />
                                        <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
                                            or paste directly
                                        </span>
                                    </div>
                                    
                                    <textarea
                                        id="json-config"
                                        className="min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                                        placeholder="Paste a URL directly or any JSON format shown above..."
                                        value={jsonConfig}
                                        onChange={(e) => setJsonConfig(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                <p>The parser will automatically detect the format and import all servers.</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 overflow-y-auto px-1 py-0.5 mb-14 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        <h3 className="text-sm font-medium">{editingServerId ? "Edit MCP Server" : "Add New MCP Server"}</h3>
                        <div className="space-y-4">
                            <div className="grid gap-1.5">
                                <Label htmlFor="name" className="flex items-center gap-2">
                                    Server Name
                                    <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="name"
                                    value={newServer.name}
                                    onChange={(e) => setNewServer({ ...newServer, name: e.target.value })}
                                    placeholder="My MCP Server"
                                    className={`relative z-0 ${formErrors.name ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                />
                                {formErrors.name && (
                                    <p className="text-xs text-destructive flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        {formErrors.name}
                                    </p>
                                )}
                            </div>

                            <div className="grid gap-1.5">
                                <Label htmlFor="transport-type">
                                    Transport Type
                                    <span className="text-destructive ml-1">*</span>
                                </Label>
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground">Choose how to connect to your MCP server:</p>
                                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                                        <button
                                            type="button"
                                            onClick={() => setNewServer({ ...newServer, type: 'sse' })}
                                            className={`flex items-start gap-3 p-4 rounded-lg text-left border transition-all ${
                                                newServer.type === 'sse' 
                                                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20' 
                                                    : 'border-border hover:border-border/80 hover:bg-muted/50'
                                            }`}
                                        >
                                            <Globe className={`h-5 w-5 shrink-0 mt-0.5 ${newServer.type === 'sse' ? 'text-primary' : 'text-muted-foreground'}`} />
                                            <div className="space-y-1">
                                                <p className="font-medium text-sm">SSE (Server-Sent Events)</p>
                                                <p className="text-xs text-muted-foreground">Connect to remote MCP servers via HTTP</p>
                                            </div>
                                        </button>
                                        
                                        <button
                                            type="button"
                                            onClick={() => setNewServer({ ...newServer, type: 'stdio' })}
                                            className={`flex items-start gap-3 p-4 rounded-lg text-left border transition-all ${
                                                newServer.type === 'stdio' 
                                                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20' 
                                                    : 'border-border hover:border-border/80 hover:bg-muted/50'
                                            }`}
                                        >
                                            <Terminal className={`h-5 w-5 shrink-0 mt-0.5 ${newServer.type === 'stdio' ? 'text-primary' : 'text-muted-foreground'}`} />
                                            <div className="space-y-1">
                                                <p className="font-medium text-sm">stdio (Standard I/O)</p>
                                                <p className="text-xs text-muted-foreground">Run local MCP server processes</p>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {newServer.type === 'sse' ? (
                                <div className="grid gap-1.5">
                                    <Label htmlFor="url" className="flex items-center gap-2">
                                        Server URL
                                        <span className="text-destructive">*</span>
                                    </Label>
                                    <Input
                                        id="url"
                                        value={newServer.url}
                                        onChange={(e) => setNewServer({ ...newServer, url: e.target.value })}
                                        placeholder="https://mcp.example.com/token/sse"
                                        className={`relative z-0 ${formErrors.url ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                    />
                                    {formErrors.url && (
                                        <p className="text-xs text-destructive flex items-center gap-1">
                                            <AlertTriangle className="h-3 w-3" />
                                            {formErrors.url}
                                        </p>
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                        Full URL to the SSE endpoint of the MCP server
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="grid gap-1.5">
                                        <Label htmlFor="command" className="flex items-center gap-2">
                                            Command
                                            <span className="text-destructive">*</span>
                                        </Label>
                                        <Input
                                            id="command"
                                            value={newServer.command}
                                            onChange={(e) => setNewServer({ ...newServer, command: e.target.value })}
                                            placeholder="node"
                                            className={`relative z-0 ${formErrors.command ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                        />
                                        {formErrors.command && (
                                            <p className="text-xs text-destructive flex items-center gap-1">
                                                <AlertTriangle className="h-3 w-3" />
                                                {formErrors.command}
                                            </p>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                            Executable to run (e.g., node, python, npx)
                                        </p>
                                    </div>
                                    <div className="grid gap-1.5">
                                        <Label htmlFor="args" className="flex items-center gap-2">
                                            Arguments
                                            <span className="text-destructive">*</span>
                                        </Label>
                                        <Input
                                            id="args"
                                            value={newServer.args?.join(' ') || ''}
                                            onChange={(e) => handleArgsChange(e.target.value)}
                                            placeholder="src/mcp-server.js --port 3001"
                                            className={`relative z-0 ${formErrors.args ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                        />
                                        {formErrors.args && (
                                            <p className="text-xs text-destructive flex items-center gap-1">
                                                <AlertTriangle className="h-3 w-3" />
                                                {formErrors.args}
                                            </p>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                            Space-separated arguments or JSON array
                                        </p>
                                    </div>
                                </>
                            )}

                            {/* Advanced Configuration Toggle */}
                            <div className="flex items-center justify-between pt-2">
                                <Label className="text-sm font-medium">Advanced Configuration</Label>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
                                    className="gap-2 text-xs"
                                >
                                    {showAdvancedConfig ? 'Hide' : 'Show'} Advanced
                                    {showAdvancedConfig ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </Button>
                            </div>

                            {/* Advanced Configuration - only show if toggled */}
                            {showAdvancedConfig && (
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem value="env-vars">
                                        <AccordionTrigger className="text-sm py-2">
                                            Environment Variables
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="space-y-3">
                                                <div className="flex items-end gap-2">
                                                    <div className="flex-1">
                                                        <Label htmlFor="env-key" className="text-xs mb-1 block">
                                                            Key
                                                        </Label>
                                                        <Input
                                                            id="env-key"
                                                            value={newEnvVar.key}
                                                            onChange={(e) => setNewEnvVar({ ...newEnvVar, key: e.target.value })}
                                                            placeholder="API_KEY"
                                                            className="h-8 relative z-0"
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <Label htmlFor="env-value" className="text-xs mb-1 block">
                                                            Value
                                                        </Label>
                                                        <Input
                                                            id="env-value"
                                                            value={newEnvVar.value}
                                                            onChange={(e) => setNewEnvVar({ ...newEnvVar, value: e.target.value })}
                                                            placeholder="your-secret-key"
                                                            className="h-8 relative z-0"
                                                            type="text"
                                                        />
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={addEnvVar}
                                                        disabled={!newEnvVar.key}
                                                        className="h-8 mt-1"
                                                    >
                                                        <Plus className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>

                                                {newServer.env && newServer.env.length > 0 ? (
                                                    <div className="border rounded-md divide-y">
                                                        {newServer.env.map((env, index) => (
                                                            <div key={index} className="flex items-center justify-between p-2 text-sm">
                                                                <div className="flex-1 flex items-center gap-1 truncate">
                                                                    <span className="font-mono text-xs">{env.key}</span>
                                                                    <span className="mx-2 text-muted-foreground">=</span>
                                                                    
                                                                    {editingEnvIndex === index ? (
                                                                        <div className="flex gap-1 flex-1">
                                                                            <Input
                                                                                className="h-6 text-xs py-1 px-2"
                                                                                value={editedEnvValue}
                                                                                onChange={(e) => setEditedEnvValue(e.target.value)}
                                                                                onKeyDown={(e) => e.key === 'Enter' && saveEditedEnvValue()}
                                                                                autoFocus
                                                                            />
                                                                            <Button 
                                                                                size="sm" 
                                                                                className="h-6 px-2"
                                                                                onClick={saveEditedEnvValue}
                                                                            >
                                                                                Save
                                                                            </Button>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <span className="text-xs text-muted-foreground truncate">
                                                                                {isSensitiveKey(env.key) && !showSensitiveEnvValues[index] 
                                                                                    ? maskValue(env.value) 
                                                                                    : env.value}
                                                                            </span>
                                                                            <span className="flex ml-1 gap-1">
                                                                                {isSensitiveKey(env.key) && (
                                                                                    <button
                                                                                        onClick={() => toggleSensitiveEnvValue(index)}
                                                                                        className="p-1 hover:bg-muted/50 rounded-full"
                                                                                    >
                                                                                        {showSensitiveEnvValues[index] ? (
                                                                                            <EyeOff className="h-3 w-3 text-muted-foreground" />
                                                                                        ) : (
                                                                                            <Eye className="h-3 w-3 text-muted-foreground" />
                                                                                        )}
                                                                                    </button>
                                                                                )}
                                                                                <button
                                                                                    onClick={() => startEditEnvValue(index, env.value)}
                                                                                    className="p-1 hover:bg-muted/50 rounded-full"
                                                                                >
                                                                                    <Edit2 className="h-3 w-3 text-muted-foreground" />
                                                                                </button>
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => removeEnvVar(index)}
                                                                    className="h-6 w-6 p-0 ml-2"
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-muted-foreground text-center py-2">
                                                        No environment variables added
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    Environment variables will be passed to the MCP server process.
                                                </p>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>

                                    <AccordionItem value="headers">
                                        <AccordionTrigger className="text-sm py-2">
                                            {newServer.type === 'sse' ? 'HTTP Headers' : 'Additional Configuration'}
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="space-y-3">
                                                <div className="flex items-end gap-2">
                                                    <div className="flex-1">
                                                        <Label htmlFor="header-key" className="text-xs mb-1 block">
                                                            Key
                                                        </Label>
                                                        <Input
                                                            id="header-key"
                                                            value={newHeader.key}
                                                            onChange={(e) => setNewHeader({ ...newHeader, key: e.target.value })}
                                                            placeholder="Authorization"
                                                            className="h-8 relative z-0"
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <Label htmlFor="header-value" className="text-xs mb-1 block">
                                                            Value
                                                        </Label>
                                                        <Input
                                                            id="header-value"
                                                            value={newHeader.value}
                                                            onChange={(e) => setNewHeader({ ...newHeader, value: e.target.value })}
                                                            placeholder="Bearer token123"
                                                            className="h-8 relative z-0"
                                                        />
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={addHeader}
                                                        disabled={!newHeader.key}
                                                        className="h-8 mt-1"
                                                    >
                                                        <Plus className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>

                                                {newServer.headers && newServer.headers.length > 0 ? (
                                                    <div className="border rounded-md divide-y">
                                                        {newServer.headers.map((header, index) => (
                                                            <div key={index} className="flex items-center justify-between p-2 text-sm">
                                                                <div className="flex-1 flex items-center gap-1 truncate">
                                                                    <span className="font-mono text-xs">{header.key}</span>
                                                                    <span className="mx-2 text-muted-foreground">:</span>
                                                                    
                                                                    {editingHeaderIndex === index ? (
                                                                        <div className="flex gap-1 flex-1">
                                                                            <Input
                                                                                className="h-6 text-xs py-1 px-2"
                                                                                value={editedHeaderValue}
                                                                                onChange={(e) => setEditedHeaderValue(e.target.value)}
                                                                                onKeyDown={(e) => e.key === 'Enter' && saveEditedHeaderValue()}
                                                                                autoFocus
                                                                            />
                                                                            <Button 
                                                                                size="sm" 
                                                                                className="h-6 px-2"
                                                                                onClick={saveEditedHeaderValue}
                                                                            >
                                                                                Save
                                                                            </Button>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <span className="text-xs text-muted-foreground truncate">
                                                                                {isSensitiveKey(header.key) && !showSensitiveHeaderValues[index] 
                                                                                    ? maskValue(header.value) 
                                                                                    : header.value}
                                                                            </span>
                                                                            <span className="flex ml-1 gap-1">
                                                                                {isSensitiveKey(header.key) && (
                                                                                    <button
                                                                                        onClick={() => toggleSensitiveHeaderValue(index)}
                                                                                        className="p-1 hover:bg-muted/50 rounded-full"
                                                                                    >
                                                                                        {showSensitiveHeaderValues[index] ? (
                                                                                            <EyeOff className="h-3 w-3 text-muted-foreground" />
                                                                                        ) : (
                                                                                            <Eye className="h-3 w-3 text-muted-foreground" />
                                                                                        )}
                                                                                    </button>
                                                                                )}
                                                                                <button
                                                                                    onClick={() => startEditHeaderValue(index, header.value)}
                                                                                    className="p-1 hover:bg-muted/50 rounded-full"
                                                                                >
                                                                                    <Edit2 className="h-3 w-3 text-muted-foreground" />
                                                                                </button>
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => removeHeader(index)}
                                                                    className="h-6 w-6 p-0 ml-2"
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-muted-foreground text-center py-2">
                                                        No {newServer.type === 'sse' ? 'headers' : 'additional configuration'} added
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    {newServer.type === 'sse'
                                                        ? 'HTTP headers will be sent with requests to the SSE endpoint.'
                                                        : 'Additional configuration parameters for the stdio transport.'}
                                                </p>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            )}
                        </div>
                    </div>
                )}

                {/* Improved footer with better mobile layout */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-sm border-t border-border flex flex-col sm:flex-row justify-between gap-3 z-10">
                    {view === 'list' ? (
                        <>
                            <div className="flex gap-2 order-2 sm:order-1">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="sm" className="gap-1.5">
                                            <Settings className="h-3.5 w-3.5" />
                                            <span className="hidden sm:inline">Actions</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start">
                                        <DropdownMenuItem 
                                            onClick={exportServers}
                                            disabled={servers.length === 0}
                                            className="hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground"
                                        >
                                            <Download className="h-4 w-4 mr-2" />
                                            Export Servers
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                            onClick={cleanupSandboxes}
                                            disabled={isCleaningUp}
                                            className="hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground"
                                        >
                                            <RefreshCw className={`h-4 w-4 mr-2 ${isCleaningUp ? 'animate-spin' : ''}`} />
                                            {isCleaningUp ? 'Cleaning...' : 'Cleanup Sandboxes'}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={deleteAllServers}
                                            disabled={servers.length === 0}
                                            className="text-destructive focus:text-destructive hover:bg-destructive/10 focus:bg-destructive/10"
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Delete All Servers
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                
                                <Button
                                    variant="outline"
                                    onClick={() => setView('import')}
                                    size="sm"
                                    className="gap-1.5"
                                >
                                    <Upload className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">Import</span>
                                </Button>
                            </div>
                            
                            <Button
                                onClick={() => setView('add')}
                                size="sm"
                                className="gap-1.5 order-1 sm:order-2"
                            >
                                <PlusCircle className="h-3.5 w-3.5" />
                                Add Server
                            </Button>
                        </>
                    ) : view === 'import' ? (
                        <>
                            <Button variant="outline" onClick={handleFormCancel} className="order-2 sm:order-1">
                                Cancel
                            </Button>
                            <Button
                                onClick={importFromJson}
                                disabled={!jsonConfig.trim()}
                                className="order-1 sm:order-2"
                            >
                                Import Servers
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={handleFormCancel} className="order-2 sm:order-1">
                                Cancel
                            </Button>
                            <Button
                                onClick={editingServerId ? updateServer : addServer}
                                disabled={Object.keys(formErrors).length > 0 || isValidating}
                                className="order-1 sm:order-2 gap-2"
                            >
                                {isValidating && <RefreshCw className="h-3 w-3 animate-spin" />}
                                {editingServerId ? "Save Changes" : "Add Server"}
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}; 
